import React, { useState, useRef, useEffect } from 'react'
import { Ruler, Target, RotateCcw, CheckCircle, Info } from 'lucide-react'
import { gsap } from 'gsap'

interface InteractiveDiscoveryProps {
  data: {
    title: string
    instruction: string
    interactive_type: string
    simulation_config: any
    discovery_questions: Array<{
      question: string
      options: string[]
      correct: number
      explanation: string
    }>
    key_concept: string
  }
  onComplete: () => void
}

const InteractiveDiscovery: React.FC<InteractiveDiscoveryProps> = ({ data, onComplete }) => {
  const [pendulumAngle, setPendulumAngle] = useState(0)
  const [showRuler, setShowRuler] = useState(false)
  const [showAmplitude, setShowAmplitude] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [userAnswer, setUserAnswer] = useState<number | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [hasAnswered, setHasAnswered] = useState(false)
  
  const pendulumRef = useRef<HTMLDivElement>(null)
  const bobRef = useRef<HTMLDivElement>(null)
  const stringRef = useRef<HTMLDivElement>(null)
  const rulerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(containerRef.current, 
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" }
      )
    }
  }, [])

  const handleMouseDown = (e: React.MouseEvent) => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!pendulumRef.current) return
    
    const rect = pendulumRef.current.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    
    const deltaX = e.clientX - centerX
    const deltaY = e.clientY - centerY
    
    // Calculate angle (limit to reasonable range)
    let angle = Math.atan2(deltaX, deltaY) * (180 / Math.PI)
    angle = Math.max(-60, Math.min(60, angle))
    
    setPendulumAngle(angle)
    updatePendulumPosition(angle)
    
    // Show ruler and amplitude markers
    if (!showRuler) {
      setShowRuler(true)
      setShowAmplitude(true)
    }
  }

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  const updatePendulumPosition = (angle: number) => {
    if (stringRef.current && bobRef.current) {
      const radians = (angle * Math.PI) / 180
      const length = 80 // string length in pixels
      
      const x = Math.sin(radians) * length
      const y = Math.cos(radians) * length
      
      gsap.to(stringRef.current, {
        rotation: angle,
        duration: 0.3,
        ease: "power2.out"
      })
      
      gsap.to(bobRef.current, {
        x: x,
        y: y,
        duration: 0.3,
        ease: "power2.out"
      })
    }
  }

  const resetPendulum = () => {
    setPendulumAngle(0)
    setShowRuler(false)
    setShowAmplitude(false)
    setCurrentQuestion(0)
    setUserAnswer(null)
    setShowFeedback(false)
    setHasAnswered(false)
    
    if (stringRef.current && bobRef.current) {
      gsap.to(stringRef.current, {
        rotation: 0,
        duration: 0.5,
        ease: "power2.out"
      })
      
      gsap.to(bobRef.current, {
        x: 0,
        y: 0,
        duration: 0.5,
        ease: "power2.out"
      })
    }
  }

  const handleAnswerSelect = (answerIndex: number) => {
    // ✅ Prevent multiple clicks
    if (hasAnswered) {
      console.log('🚫 Already answered, ignoring click')
      return
    }
    
    console.log(`🎯 InteractiveDiscovery: Answer selected ${answerIndex}`)
    setUserAnswer(answerIndex)
    setShowFeedback(true)
    setHasAnswered(true)
  }

  const handleNextQuestion = () => {
    if (currentQuestion < data.discovery_questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
      setUserAnswer(null)
      setShowFeedback(false)
      setHasAnswered(false)
    } else {
      // All questions answered, complete the component
      onComplete()
    }
  }

  const currentQ = data.discovery_questions[currentQuestion]
  const isCorrect = userAnswer === currentQ.correct

  return (
    <div ref={containerRef} className="brilliant-card p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-dark-900 mb-4">
          {data.title}
        </h2>
        <div className="w-24 h-1 bg-gradient-brilliant mx-auto rounded-full"></div>
      </div>

      {/* Instruction */}
      <div className="bg-gradient-to-r from-secondary-50 to-blue-50 p-6 rounded-2xl border border-secondary-200 mb-8">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Ruler className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-dark-900 mb-3">
              Hướng dẫn
            </h3>
            <p className="text-dark-700 text-lg leading-relaxed">
              {data.instruction}
            </p>
          </div>
        </div>
      </div>

      {/* Interactive Pendulum with Ruler */}
      <div className="bg-white rounded-2xl border border-light-200 p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-bold text-dark-900">Con lắc với thước đo</h4>
          <button
            onClick={resetPendulum}
            className="p-3 bg-light-100 text-dark-600 hover:bg-light-200 hover:shadow-md rounded-xl transition-all duration-300 hover:scale-105"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>

        {/* Pendulum Area */}
        <div className="flex justify-center mb-6">
          <div 
            ref={pendulumRef}
            className="relative w-64 h-64 border-2 border-dashed border-light-300 rounded-full flex items-center justify-center"
            style={{ cursor: 'grab' }}
            onMouseDown={handleMouseDown}
          >
            {/* Equilibrium position indicator */}
            <div className="absolute top-0 left-1/2 w-1 h-32 bg-gradient-to-b from-accent-green to-green-500 transform -translate-x-1/2 opacity-60"></div>
            
            {/* Pendulum string */}
            <div 
              ref={stringRef}
              className="absolute top-0 left-1/2 w-1 h-20 bg-dark-600 transform -translate-x-1/2 origin-top"
            ></div>
            
            {/* Pendulum bob */}
            <div 
              ref={bobRef}
              className="absolute top-20 left-1/2 w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full transform -translate-x-1/2 shadow-lg"
            ></div>
            
            {/* Ruler */}
            {showRuler && (
              <div 
                ref={rulerRef}
                className="absolute top-20 left-1/2 w-32 h-1 bg-gradient-to-r from-secondary-400 to-secondary-600 transform -translate-x-1/2"
                style={{
                  transform: `translate(-50%, -50%) rotate(${pendulumAngle}deg)`
                }}
              >
                {/* Ruler markings */}
                <div className="absolute top-0 left-0 w-full h-full">
                  {[-60, -40, -20, 0, 20, 40, 60].map((mark) => (
                    <div
                      key={mark}
                      className="absolute top-0 w-0.5 h-3 bg-secondary-600 transform -translate-x-1/2"
                      style={{
                        left: `${((mark + 60) / 120) * 100}%`
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Amplitude marker */}
            {showAmplitude && (
              <div className="absolute top-20 left-1/2 w-2 h-2 bg-accent-orange rounded-full transform -translate-x-1/2 animate-pulse">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-bold text-accent-orange">
                  A
                </div>
              </div>
            )}
            
            {/* Angle indicator */}
            <div className="absolute top-4 right-4 bg-white px-3 py-2 rounded-lg border border-light-200 shadow-sm">
              <span className="text-sm font-bold text-dark-700">
                {Math.abs(pendulumAngle).toFixed(1)}°
              </span>
            </div>
          </div>
        </div>

        {/* Interaction Status */}
        <div className="text-center">
          {!showRuler ? (
            <p className="text-dark-600 italic">Hãy kéo con lắc để hiển thị thước đo</p>
          ) : (
            <div className="space-y-3">
              <p className="text-primary-600 font-medium">
                ✅ Thước đo đã hiển thị! Quan sát ly độ và biên độ
              </p>
              <p className="text-secondary-600 font-medium">
                💡 Biên độ A là khoảng cách xa nhất từ vị trí cân bằng
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Discovery Question */}
      <div className="bg-gradient-to-r from-primary-50 to-primary-100 p-6 rounded-2xl border border-primary-200 mb-6">
        <h4 className="font-bold text-primary-800 mb-4 flex items-center">
          <Target className="w-5 h-5 mr-2" />
          Câu hỏi khám phá {currentQuestion + 1}/{data.discovery_questions.length}:
        </h4>
        <p className="text-primary-700 text-lg mb-6">
          {currentQ.question}
        </p>
        
        {/* Answer Options */}
        <div className="space-y-3">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleAnswerSelect(index)
              }}
              disabled={hasAnswered}
              type="button"
              className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-300 ${
                userAnswer === index
                  ? isCorrect
                    ? 'border-accent-green bg-gradient-to-r from-accent-green/10 to-green-50 shadow-lg shadow-green-500/20'
                    : 'border-red-500 bg-gradient-to-r from-red-50 to-red-100 shadow-lg shadow-red-500/20'
                  : 'border-light-300 hover:border-primary-400 hover:bg-primary-25 hover:shadow-md'
              } ${hasAnswered ? 'cursor-default' : 'cursor-pointer hover:scale-[1.02]'}`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                  userAnswer === index
                    ? isCorrect
                      ? 'border-accent-green bg-gradient-to-br from-accent-green to-green-600 text-white shadow-md'
                      : 'border-red-500 bg-gradient-to-br from-red-500 to-red-600 text-white shadow-md'
                    : 'border-light-400'
                }`}>
                  {userAnswer === index && (
                    <span className="text-sm font-bold">
                      {isCorrect ? '✓' : '✗'}
                    </span>
                  )}
                </div>
                <span className="text-dark-700 flex-1 text-lg font-medium">{option}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Feedback */}
        {showFeedback && (
          <div className={`mt-6 p-4 rounded-xl border-2 ${
            isCorrect 
              ? 'bg-gradient-to-r from-accent-green/10 to-green-50 border-green-300' 
              : 'bg-gradient-to-r from-accent-orange/10 to-orange-50 border-orange-300'
          }`}>
            <div className="flex items-center space-x-3">
              {isCorrect ? (
                <CheckCircle className="w-6 h-6 text-accent-green flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 text-accent-orange flex-shrink-0">✗</div>
              )}
              <div>
                <h5 className={`font-bold mb-2 ${
                  isCorrect ? 'text-green-800' : 'text-orange-800'
                }`}>
                  {isCorrect ? 'Chính xác!' : 'Chưa đúng'}
                </h5>
                <p className={`${
                  isCorrect ? 'text-green-700' : 'text-orange-700'
                }`}>
                  {currentQ.explanation}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Next Button */}
        {hasAnswered && (
          <div className="mt-6 text-center">
            <button
              onClick={handleNextQuestion}
              className="px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold text-lg rounded-xl hover:from-violet-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-2xl"
            >
              {currentQuestion < data.discovery_questions.length - 1 ? '→ Câu hỏi tiếp theo' : '✓ Hoàn thành'}
            </button>
          </div>
        )}
      </div>

      {/* Key Concept */}
      <div className="bg-gradient-to-r from-light-50 to-white p-6 rounded-2xl border border-light-200">
        <h4 className="font-bold text-dark-900 mb-3 flex items-center">
          <Info className="w-5 h-5 mr-2" />
          Khái niệm chính:
        </h4>
        <p className="text-dark-700 text-lg font-medium">
          {data.key_concept}
        </p>
      </div>
    </div>
  )
}

export default InteractiveDiscovery
